import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Table, Button, Alert, Spinner, Pagination, ButtonGroup, Form, Modal, Dropdown, InputGroup } from 'react-bootstrap';
import api from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import RefreshConnectionButton from '../components/RefreshConnectionButton';
import debounce from 'lodash.debounce';

// Fee constants (aligned with server)
const FEE_CONSTANTS = {
  PROMOTED_LISTING_RATE_MIN: 0,
  PROMOTED_LISTING_RATE_MAX: 20,
};

// EditableCostCell component (unchanged)
const EditableCostCell = ({ product, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(parseFloat(product.cost_of_goods) || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = () => setIsEditing(true);

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setValue(isNaN(newValue) ? 0 : newValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSave();
  };

  const handleBlur = () => {
    if (!isSaving) {
      setIsEditing(false);
      setValue(parseFloat(product.cost_of_goods) || 0);
    }
  };

  const handleSave = async () => {
    if (value < 0) {
      setError('Cost cannot be negative.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      console.log(`[${new Date().toISOString()}] [EditableCostCell] Saving cost for SKU: ${product.sku}, value: ${value}`);
      await onSave(product.sku, value);
      setIsEditing(false);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to save: ${errorMsg}`);
      console.error(`[${new Date().toISOString()}] [EditableCostCell] Error saving cost for SKU: ${product.sku}`, errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return isEditing ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
        autoFocus
        style={{ width: '80px' }}
        disabled={isSaving}
      />
      <Button variant="success" size="sm" onClick={handleSave} disabled={isSaving}>
        {isSaving ? <Spinner size="sm" /> : 'Save'}
      </Button>
      {error && <span style={{ color: 'red', fontSize: '12px' }}>{error}</span>}
    </div>
  ) : (
    <span onClick={handleClick} style={{ cursor: 'pointer' }}>
      {(parseFloat(product.cost_of_goods) || 0).toFixed(2)}
    </span>
  );
};

// EditableShippingCell component (unchanged)
const EditableShippingCell = ({ product, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(parseFloat(product.shippingCost) || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = () => setIsEditing(true);

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setValue(isNaN(newValue) ? 0 : newValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSave();
  };

  const handleBlur = () => {
    if (!isSaving) {
      setIsEditing(false);
      setValue(parseFloat(product.shippingCost) || 0);
    }
  };

  const handleSave = async () => {
    if (value < 0) {
      setError('Shipping cost cannot be negative.');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      console.log(`[${new Date().toISOString()}] [EditableShippingCell] Saving shipping cost for SKU: ${product.sku}, value: ${value}`);
      await onSave(product.sku, value);
      setIsEditing(false);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to save: ${errorMsg}`);
      console.error(`[${new Date().toISOString()}] [EditableShippingCell] Error saving shipping cost for SKU: ${product.sku}`, errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return isEditing ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
        autoFocus
        style={{ width: '80px' }}
        disabled={isSaving}
      />
      <Button variant="success" size="sm" onClick={handleSave} disabled={isSaving}>
        {isSaving ? <Spinner size="sm" /> : 'Save'}
      </Button>
      {error && <span style={{ color: 'red', fontSize: '12px' }}>{error}</span>}
    </div>
  ) : (
    <span onClick={handleClick} style={{ cursor: 'pointer' }}>
      {(parseFloat(product.shippingCost) || 0).toFixed(2)}
    </span>
  );
};

// EditablePromotedCell component (unchanged)
const EditablePromotedCell = ({ product, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState((parseFloat(product.promotedListingRate) * 100) || 0);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = () => setIsEditing(true);

  const handleChange = (e) => {
    const newValue = parseFloat(e.target.value);
    setValue(isNaN(newValue) ? 0 : newValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSave();
  };

  const handleBlur = () => {
    if (!isSaving) {
      setIsEditing(false);
      setValue((parseFloat(product.promotedListingRate) * 100) || 0);
    }
  };

  const handleSave = async () => {
    if (value < FEE_CONSTANTS.PROMOTED_LISTING_RATE_MIN || value > FEE_CONSTANTS.PROMOTED_LISTING_RATE_MAX) {
      setError(`Rate must be ${FEE_CONSTANTS.PROMOTED_LISTING_RATE_MIN}–${FEE_CONSTANTS.PROMOTED_LISTING_RATE_MAX}%.`);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      console.log(`[${new Date().toISOString()}] [EditablePromotedCell] Saving promoted rate for SKU: ${product.sku}, value: ${value}%`);
      await onSave(product.sku, value / 100);
      setIsEditing(false);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to save: ${errorMsg}`);
      console.error(`[${new Date().toISOString()}] [EditablePromotedCell] Error saving promoted rate for SKU: ${product.sku}`, errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  return isEditing ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
      <input
        type="number"
        step="0.1"
        min={FEE_CONSTANTS.PROMOTED_LISTING_RATE_MIN}
        max={FEE_CONSTANTS.PROMOTED_LISTING_RATE_MAX}
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onBlur={handleBlur}
        autoFocus
        style={{ width: '60px' }}
        disabled={isSaving}
      />
      <Button variant="success" size="sm" onClick={handleSave} disabled={isSaving}>
        {isSaving ? <Spinner size="sm" /> : 'Save'}
      </Button>
      {error && <span style={{ color: 'red', fontSize: '12px' }}>{error}</span>}
    </div>
  ) : (
    <span onClick={handleClick} style={{ cursor: 'pointer' }}>
      {((parseFloat(product.promotedListingRate) * 100) || 0).toFixed(1)}%
    </span>
  );
};

// AuthenticatedImage component (unchanged)
const AuthenticatedImage = ({ src, alt, sku, platform }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const FALLBACK_IMAGE = 'https://placehold.co/50x50.png';

  useEffect(() => {
    const fetchImage = async () => {
      const token = localStorage.getItem('token');
      if (!token && platform === 'eBay') {
        console.warn(`[${new Date().toISOString()}] [AuthenticatedImage] No token, redirecting to login`);
        navigate('/login');
        return;
      }

      if (src && (platform === 'Shopify' || src.startsWith('http'))) {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          console.log(`[${new Date().toISOString()}] [AuthenticatedImage] Direct image loaded for SKU: ${sku}`);
          setImageSrc(src);
        };
        img.onerror = () => {
          console.warn(`[${new Date().toISOString()}] [AuthenticatedImage] Direct load failed for ${platform} image ${sku}`);
          if (platform === 'eBay' && src) fetchViaProxy();
          else setError(true);
        };
        return;
      }

      if (platform === 'eBay' && src) {
        try {
          console.log(`[${new Date().toISOString()}] [AuthenticatedImage] Fetching eBay image for SKU ${sku} via proxy`);
          const response = await api.get(`/proxy-image?url=${encodeURIComponent(src)}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          const url = URL.createObjectURL(response.data);
          setImageSrc(url);
        } catch (err) {
          console.error(`[${new Date().toISOString()}] [AuthenticatedImage] Failed to load eBay image for SKU: ${sku}`, err.message);
          setError(true);
        }
      } else {
        setError(true);
      }
    };

    const fetchViaProxy = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get(`/proxy-image?url=${encodeURIComponent(src)}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        setImageSrc(url);
      } catch (err) {
        console.error(`[${new Date().toISOString()}] [AuthenticatedImage] Proxy image load failed for SKU: ${sku}`, err.message);
        setError(true);
      }
    };

    if (src) fetchImage();

    return () => {
      if (imageSrc && imageSrc !== src) URL.revokeObjectURL(imageSrc);
    };
  }, [src, sku, platform, navigate]);

  if (error || !src) return <img src={FALLBACK_IMAGE} alt="Default" style={{ width: '50px', height: '50px', objectFit: 'cover' }} />;
  if (!imageSrc) return <Spinner animation="border" size="sm" />;
  return <img src={imageSrc} alt={alt} style={{ width: '50px', height: '50px', objectFit: 'cover' }} />;
};

// Calculate fees with validation and retries (unchanged)
const calculateFeeClientSide = async (product, storeSubscription, sellerStatus, navigate, retryCount = 0, maxRetries = 2) => {
  console.log(`[${new Date().toISOString()}] [EbayInventory] Calculating fees for SKU: ${product.sku}, retry: ${retryCount}`);
  const price = parseFloat(product.price) || 0;
  const shippingCost = parseFloat(product.shippingCost) || 0;
  const cost = parseFloat(product.cost_of_goods) || 0;
  const promotedListingRate = parseFloat(product.promotedListingRate) || 0;

  if (!product.sku || price <= 0 || shippingCost < 0 || isNaN(price) || isNaN(shippingCost) || cost < 0) {
    console.error(`[${new Date().toISOString()}] [EbayInventory] Invalid product data for SKU: ${product.sku}`, product);
    return {
      totalFee: null,
      netProfit: null,
      roi_percentage: null,
      feesBreakdown: [],
      source: 'error',
      error: 'Invalid product data (missing SKU, invalid price, shipping, or cost)',
    };
  }

  if (promotedListingRate > FEE_CONSTANTS.PROMOTED_LISTING_RATE_MAX / 100) {
    console.error(`[${new Date().toISOString()}] [EbayInventory] Invalid promoted rate for SKU: ${product.sku}`, promotedListingRate);
    return {
      totalFee: null,
      netProfit: null,
      roi_percentage: null,
      feesBreakdown: [],
      source: 'error',
      error: `Promoted rate must be 0–${FEE_CONSTANTS.PROMOTED_LISTING_RATE_MAX}%`,
    };
  }

  try {
    const token = localStorage.getItem('token');
    const payload = {
      products: [
        {
          sku: product.sku,
          price,
          shippingCost,
          cost_of_goods: cost,
          categoryId: product.categoryId || '293',
          promotedListingRate,
          storeSubscription: storeSubscription || 'Basic',
          sellerStatus: sellerStatus || 'Standard',
          isInternationalSale: !!product.isInternationalSale,
          listingId: product.ebay_item_id || null,
        },
      ],
    };
    console.log(`[${new Date().toISOString()}] [EbayInventory] Sending payload for SKU: ${product.sku}`, payload);

    const response = await api.post('/ebay/fees', payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const fees = response.data;
    console.log(`[${new Date().toISOString()}] [EbayInventory] Received fees for SKU: ${product.sku}`, fees);

    if (
      !fees ||
      typeof fees.totalFee !== 'number' ||
      fees.totalFee < 0 ||
      isNaN(fees.totalFee) ||
      !Array.isArray(fees.feesBreakdown) ||
      typeof fees.netProfit !== 'number'
    ) {
      throw new Error('Invalid or incomplete fee data from server');
    }

    const calculatedNetProfit = price - fees.totalFee - cost;
    const calculatedRoi = cost > 0 ? ((calculatedNetProfit / cost) * 100) : 0;
    if (Math.abs(fees.netProfit - calculatedNetProfit) > 0.01) {
      console.warn(`[${new Date().toISOString()}] [EbayInventory] Net profit mismatch for SKU: ${product.sku}, server: ${fees.netProfit}, client: ${calculatedNetProfit}`);
      fees.netProfit = parseFloat(calculatedNetProfit.toFixed(2));
    }
    fees.roi_percentage = parseFloat(calculatedRoi.toFixed(2));

    return fees;
  } catch (error) {
    const errorMsg = error.response?.data?.error || error.message;
    console.error(`[${new Date().toISOString()}] [EbayInventory] Error fetching fees for SKU: ${product.sku}: ${errorMsg}`);

    if (error.response?.status === 401) {
      console.warn(`[${new Date().toISOString()}] [EbayInventory] 401 Unauthorized, redirecting to login`);
      localStorage.removeItem('token');
      navigate('/login');
      return {
        totalFee: null,
        netProfit: null,
        roi_percentage: null,
        feesBreakdown: [],
        source: 'error',
        error: 'Authorization required',
      };
    }

    if (error.response?.status === 429 && retryCount < maxRetries) {
      console.log(`[${new Date().toISOString()}] [EbayInventory] Rate limit hit, retrying (${retryCount + 1}/${maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
      return calculateFeeClientSide(product, storeSubscription, sellerStatus, navigate, retryCount + 1, maxRetries);
    }

    return {
      totalFee: null,
      netProfit: null,
      roi_percentage: null,
      feesBreakdown: [],
      source: 'error',
      error: `Fee calculation failed: ${errorMsg}`,
    };
  }
};

// Main EbayInventory component with enhanced search and pagination
function EbayInventory() {
  const [products, setProducts] = useState(() => {
    try {
      const saved = localStorage.getItem('ebayProducts');
      if (!saved) return [];
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) {
        console.warn(`[${new Date().toISOString()}] [EbayInventory] Invalid localStorage data, clearing`);
        localStorage.removeItem('ebayProducts');
        return [];
      }
      return parsed.filter(p => p.sku && typeof p.cost_of_goods === 'number' && typeof p.shippingCost === 'number');
    } catch (err) {
      console.error(`[${new Date().toISOString()}] [EbayInventory] Failed to parse localStorage: ${err.message}`);
      localStorage.removeItem('ebayProducts');
      return [];
    }
  });
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(100); // Fixed items per page
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    minStock: '',
    maxStock: '',
    platform: 'all',
  });
  const [loadingFees, setLoadingFees] = useState(new Set());
  const [storeSubscription, setStoreSubscription] = useState('Basic');
  const [sellerStatus, setSellerStatus] = useState('Standard');
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [selectedFees, setSelectedFees] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Client-side filtering function
  const filterProductsClientSide = useCallback((products, query, filters) => {
    return products.filter(product => {
      const queryLower = query.toLowerCase();
      const matchesQuery = !query || (
        product.sku?.toLowerCase().includes(queryLower) ||
        product.name?.toLowerCase().includes(queryLower)
      );

      const price = parseFloat(product.price) || 0;
      const stock = parseInt(product.stock) || 0;
      const minPrice = parseFloat(filters.minPrice) || 0;
      const maxPrice = parseFloat(filters.maxPrice) || Infinity;
      const minStock = parseInt(filters.minStock) || 0;
      const maxStock = parseInt(filters.maxStock) || Infinity;
      const platform = filters.platform;

      const matchesPrice = price >= minPrice && price <= maxPrice;
      const matchesStock = stock >= minStock && stock <= maxStock;
      const matchesPlatform = platform === 'all' || product.platform?.includes(platform);

      return matchesQuery && matchesPrice && matchesStock && matchesPlatform;
    });
  }, []);

  // Autocomplete suggestions
  const getSuggestions = useCallback((query) => {
    if (!query) return [];
    const queryLower = query.toLowerCase();
    const uniqueSuggestions = new Set();
    products.forEach(p => {
      if (p.sku?.toLowerCase().includes(queryLower) && uniqueSuggestions.size < 5) {
        uniqueSuggestions.add(p.sku);
      }
      if (p.name?.toLowerCase().includes(queryLower) && uniqueSuggestions.size < 5) {
        uniqueSuggestions.add(p.name);
      }
    });
    return Array.from(uniqueSuggestions);
  }, [products]);

  // Debounced server-side fetch
  const debouncedFetchProducts = useMemo(() => debounce(async (pageNum, query, filters) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setSearchLoading(true);
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: pageNum.toString(),
        ...(query && { search: query }),
        ...(filters.minPrice && { minPrice: filters.minPrice }),
        ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
        ...(filters.minStock && { minStock: filters.minStock }),
        ...(filters.maxStock && { maxStock: filters.maxStock }),
        platform: 'eBay',
      });
      console.log(`[${new Date().toISOString()}] [EbayInventory] Fetching products with params: ${queryParams.toString()}`);
      const response = await api.get(`/ebay/products?${queryParams.toString()}`, {

        headers: { Authorization: `Bearer ${token}` },
      });
      const ebayProducts = response.data.products.filter(p => p.platform?.includes('eBay'));

      const savedProducts = JSON.parse(localStorage.getItem('ebayProducts') || '[]');
      const savedProductsMap = new Map(savedProducts.map(p => [p.sku, {
        cost_of_goods: p.cost_of_goods,
        shippingCost: p.shippingCost,
        fees: p.fees,
        promotedListingRate: p.promotedListingRate,
        isInternationalSale: p.isInternationalSale,
      }]));

      const productsWithData = await Promise.all(ebayProducts.map(async product => {
        const saved = savedProductsMap.get(product.sku) || {};
        const updatedProduct = {
          ...product,
          cost_of_goods: parseFloat(saved.cost_of_goods) || parseFloat(product.cost_of_goods) || 0,
          shippingCost: parseFloat(saved.shippingCost) || parseFloat(product.shippingCost) || 0,
          price: parseFloat(product.price) || 0,
          categoryId: product.categoryId || '293',
          promotedListingRate: parseFloat(saved.promotedListingRate) || parseFloat(product.promotedListingRate) || 0,
          isInternationalSale: saved.isInternationalSale ?? (product.isInternationalSale || false),
          ebay_item_id: product.ebay_item_id || null,
          displayImage: product.image || (Array.isArray(product.ebay_images) ? product.ebay_images[0] : null),
          name: product.name || 'Unknown',
          sku: product.sku || 'N/A',
          platform: product.platform || 'eBay',
          stock: parseInt(product.stock) || 0,
          fees: saved.fees ?? null,
        };
        if (!updatedProduct.fees) {
          setLoadingFees(prev => new Set(prev).add(product.sku));
          const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
          setLoadingFees(prev => {
            const newSet = new Set(prev);
            newSet.delete(product.sku);
            return newSet;
          });
          updatedProduct.fees = fees;
        }
        return updatedProduct;
      }));

      setFilteredProducts(productsWithData);

      // ---- robust total & pages (prefer server totals; never use page length) ----
const hdr = response.headers || {};
const totalFromHeader = Number(
  hdr['x-total-ebay-count'] ?? hdr['x-total-count'] ?? hdr['X-Total-Count']
) || 0;

const body = response.data || {};
const totalFromBody =
  Number(body.totalEbayItems) ||
  Number(body?.totals?.eBay) ||
  Number(body.totalItems) ||
  Number(body.total) ||
  0;

// If server sent pages/limit, derive a total as a fallback
const limitFromBody = Number(body.limit) || Number(itemsPerPage);
const pagesFromBody = Number(body.totalPages) || 0;
const totalFromPages = pagesFromBody > 0 && limitFromBody > 0
  ? pagesFromBody * limitFromBody
  : 0;

// Choose the best available total
const effectiveTotal = totalFromHeader || totalFromBody || totalFromPages || 0;

setTotalItems(effectiveTotal);
setTotalPages(pagesFromBody || Math.max(1, Math.ceil(Math.max(effectiveTotal, 1) / limitFromBody)));

// (Optional) debug
console.log('[Totals:debounced]', {
  header: totalFromHeader,
  body: totalFromBody,
  derived: totalFromPages,
  effectiveTotal,
  pagesFromBody,
  limitFromBody
});

      

      setProducts(prev => {
        const newProducts = [...prev.filter(p => !productsWithData.some(np => np.sku === p.sku)), ...productsWithData];
        localStorage.setItem('ebayProducts', JSON.stringify(newProducts));
        return newProducts;
      });
      if (productsWithData.length === 0) {
        setError('No eBay products found for the search criteria.');
      } else {
        setError(null);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Search error: ${errorMsg}`);
      setError(`Failed to search inventory: ${errorMsg}`);
      setTotalItems(0);
    } finally {
      setSearchLoading(false);
    }
  }, 300), [storeSubscription, sellerStatus, itemsPerPage]);

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSuggestions(query.length > 0);
    setPage(1);
    debouncedFetchProducts(1, query, filters);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      setPage(1);
      debouncedFetchProducts(1, searchQuery, newFilters);
      return newFilters;
    });
  };

  // Handle suggestion selection
  const handleSelectSuggestion = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setPage(1);
    debouncedFetchProducts(1, suggestion, filters);
  };

  // Clear all filters and search
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilters({
      minPrice: '',
      maxPrice: '',
      minStock: '',
      maxStock: '',
      platform: 'all',
    });
    setShowSuggestions(false);
    setPage(1);
    debouncedFetchProducts(1, '', {
      minPrice: '',
      maxPrice: '',
      minStock: '',
      maxStock: '',
      platform: 'all',
    });
  };

  const checkAuthAndRedirect = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn(`[${new Date().toISOString()}] [EbayInventory] No token, redirecting to login`);
      navigate('/login');
      return false;
    }
    return true;
  };

  const checkConnection = async (retryCount = 0, maxRetries = 3) => {
  if (!checkAuthAndRedirect()) return;
  try {
    setConnectionLoading(true);
    const token = localStorage.getItem('token');
    console.log(`[${new Date().toISOString()}] [EbayInventory] Checking eBay connection`);
    const response = await api.get('/ebay/status', {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.data.connected) {
      setIsConnected(true);
      setError(null);
    } else {
      setIsConnected(false);
      setError('eBay not connected. Please reconnect.');
      // ❌ remove navigate('/auth/ebay')
      // Instead, just keep them on the page and show reconnect button
    }
  } catch (err) {
    console.error(`[EbayInventory] Error checking connection:`, err.message);
    setIsConnected(false);
    setError('Failed to check eBay connection.');
  } finally {
    setConnectionLoading(false);
  }
};


  const refreshConnection = async () => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log(`[${new Date().toISOString()}] [EbayInventory] Refreshing eBay connection`);
      const response = await api.post('/api/refresh-connection', { platform: 'eBay' }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
        return;
      }
      setError(null);
      alert(response.data.message);
      await debouncedFetchProducts(page, searchQuery, filters);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Error refreshing connection: ${errorMsg}`);
      setError(`Failed to refresh connection: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const checkImportStatus = async () => {
    if (!checkAuthAndRedirect()) return;
    try {
      const token = localStorage.getItem('token');
      console.log(`[${new Date().toISOString()}] [EbayInventory] Checking import status`);
      const response = await api.get('/api/ebay/import/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.message) setError(response.data.message);
      if (response.data.status === 'success' || response.data.status === 'warning') {
        await refreshConnection();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Error checking import status: ${errorMsg}`);
      setError(`Failed to check import status: ${errorMsg}`);
    }
  };

  const fetchProducts = async (pageNum = 1) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log(`[${new Date().toISOString()}] [EbayInventory] Fetching products, page: ${pageNum}`);
      const queryParams = new URLSearchParams({
        limit: itemsPerPage.toString(),
        page: pageNum.toString(),
        ...(searchQuery && { search: searchQuery }),
        ...(filters.minPrice && { minPrice: filters.minPrice }),
        ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
        ...(filters.minStock && { minStock: filters.minStock }),
        ...(filters.maxStock && { maxStock: filters.maxStock }),
        platform: 'eBay',

      });
      const response = await api.get(`/ebay/products?${queryParams.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ebayProducts = response.data.products.filter(p => p.platform?.includes('eBay'));

      const savedProducts = JSON.parse(localStorage.getItem('ebayProducts') || '[]');
      const savedProductsMap = new Map(savedProducts.map(p => [p.sku, {
        cost_of_goods: p.cost_of_goods,
        shippingCost: p.shippingCost,
        fees: p.fees,
        promotedListingRate: p.promotedListingRate,
        isInternationalSale: p.isInternationalSale,
      }]));

      const productsWithData = await Promise.all(ebayProducts.map(async product => {
        const saved = savedProductsMap.get(product.sku) || {};
        const updatedProduct = {
          ...product,
          cost_of_goods: parseFloat(saved.cost_of_goods) || parseFloat(product.cost_of_goods) || 0,
          shippingCost: parseFloat(saved.shippingCost) || parseFloat(product.shippingCost) || 0,
          price: parseFloat(product.price) || 0,
          categoryId: product.categoryId || '293',
          promotedListingRate: parseFloat(saved.promotedListingRate) || parseFloat(product.promotedListingRate) || 0,
          isInternationalSale: saved.isInternationalSale ?? (product.isInternationalSale || false),
          ebay_item_id: product.ebay_item_id || null,
          displayImage: product.image || (Array.isArray(product.ebay_images) ? product.ebay_images[0] : null),
          name: product.name || 'Unknown',
          sku: product.sku || 'N/A',
          platform: product.platform || 'eBay',
          stock: parseInt(product.stock) || 0,
          fees: saved.fees ?? null,
        };
        if (!updatedProduct.fees) {
          setLoadingFees(prev => new Set(prev).add(product.sku));
          const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
          setLoadingFees(prev => {
            const newSet = new Set(prev);
            newSet.delete(product.sku);
            return newSet;
          });
          updatedProduct.fees = fees;
        }
        return updatedProduct;
      }));

      setFilteredProducts(productsWithData);
      // ---- robust total & pages (prefer server totals; never use page length) ----
const hdr = response.headers || {};
const totalFromHeader = Number(
  hdr['x-total-ebay-count'] ?? hdr['x-total-count'] ?? hdr['X-Total-Count']
) || 0;

const body = response.data || {};
const totalFromBody =
  Number(body.totalEbayItems) ||
  Number(body?.totals?.eBay) ||
  Number(body.totalItems) ||
  Number(body.total) ||
  0;

// If server sent pages/limit, derive a total as a fallback
const limitFromBody = Number(body.limit) || Number(itemsPerPage);
const pagesFromBody = Number(body.totalPages) || 0;
const totalFromPages = pagesFromBody > 0 && limitFromBody > 0
  ? pagesFromBody * limitFromBody
  : 0;

// Choose the best available total
const effectiveTotal = totalFromHeader || totalFromBody || totalFromPages || 0;

setTotalItems(effectiveTotal);
setTotalPages(pagesFromBody || Math.max(1, Math.ceil(Math.max(effectiveTotal, 1) / limitFromBody)));

// (Optional) debug
console.log('[Totals]', {
  header: totalFromHeader,
  body: totalFromBody,
  derived: totalFromPages,
  effectiveTotal,
  pagesFromBody,
  limitFromBody
});

      setProducts(prev => {
        const newProducts = [...prev.filter(p => !productsWithData.some(np => np.sku === p.sku)), ...productsWithData];
        localStorage.setItem('ebayProducts', JSON.stringify(newProducts));
        return newProducts;
      });
      if (productsWithData.length === 0) {
        setError('No eBay products found. Try importing.');
      } else {
        setError(null);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Error fetching products: ${errorMsg}`);
      setError(`Failed to load inventory: ${errorMsg}`);
      setFilteredProducts([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateFees = async (product) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoadingFees(prev => new Set(prev).add(product.sku));
      const fees = await calculateFeeClientSide(product, storeSubscription, sellerStatus, navigate);
      if (fees.error) {
        setError(`Failed to calculate fees for ${product.sku}: ${fees.error}`);
        return;
      }
      const updatedProducts = products.map(p =>
        p.sku === product.sku ? { ...p, fees } : p
      );
      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(p => filteredProducts.some(fp => fp.sku === p.sku)));
      localStorage.setItem('ebayProducts', JSON.stringify(updatedProducts));
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to calculate fees for ${product.sku}: ${errorMsg}`);
    } finally {
      setLoadingFees(prev => {
        const newSet = new Set(prev);
        newSet.delete(product.sku);
        return newSet;
      });
    }
  };

  const handleImportEbay = async (retryCount = 0, maxRetries = 3) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/ebay/import', {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(response.data.message);
      await fetchProducts(page);
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Import error: ${errorMsg}`);
      if (err.response?.status === 401 && err.response.data.redirect) {
        window.location.href = err.response.data.redirect;
        return;
      }
      if (retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return handleImportEbay(retryCount + 1, maxRetries);
      }
      setError(`Failed to import eBay inventory: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCost = async (sku, newCost) => {
    if (!checkAuthAndRedirect()) return;
    try {
      const token = localStorage.getItem('token');
      console.log(`[${new Date().toISOString()}] [EbayInventory] Attempting to save cost for SKU: ${sku}, cost_of_goods: ${newCost}`);
      const response = await api.put(`/products/${sku}`, { cost_of_goods: newCost }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(`[${new Date().toISOString()}] [EbayInventory] Cost update response for SKU: ${sku}`, response.data);

      const updatedProducts = await Promise.all(products.map(async p => {
        if (p.sku === sku) {
          let updatedProduct = { ...p, cost_of_goods: newCost };
          setLoadingFees(prev => new Set(prev).add(sku));
          try {
            const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
            if (fees.error) {
              console.warn(`[${new Date().toISOString()}] [EbayInventory] Fee calculation failed for SKU: ${sku}`, fees.error);
              setError(`Failed to calculate fees for ${sku}: ${fees.error}`);
            }
            setLoadingFees(prev => {
              const newSet = new Set(prev);
              newSet.delete(sku);
              return newSet;
            });
            return { ...updatedProduct, fees };
          } catch (feeError) {
            console.error(`[${new Date().toISOString()}] [EbayInventory] Fee calculation error for SKU: ${sku}`, feeError.message);
            setLoadingFees(prev => {
              const newSet = new Set(prev);
              newSet.delete(sku);
              return newSet;
            });
            setError(`Failed to calculate fees for ${sku}: ${feeError.message}`);
            return { ...updatedProduct, fees: { error: feeError.message, totalFee: null, netProfit: null, roi_percentage: null, feesBreakdown: [] } };
          }
        }
        return p;
      }));

      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(p => filteredProducts.some(fp => fp.sku === p.sku)));
      localStorage.setItem('ebayProducts', JSON.stringify(updatedProducts));
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      console.error(`[${new Date().toISOString()}] [EbayInventory] Cost update error for SKU: ${sku}`, { error: errorMsg, status: err.response?.status, response: err.response?.data });
      setError(`Failed to update cost for ${sku}: ${errorMsg}`);
      throw err;
    }
  };

  const handleSaveShipping = async (sku, newShippingCost) => {
    if (!checkAuthAndRedirect()) return;
    try {
      const token = localStorage.getItem('token');
      await api.put(`products/${sku}`, { shippingCost: newShippingCost }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedProducts = await Promise.all(products.map(async p => {
        if (p.sku === sku) {
          let updatedProduct = { ...p, shippingCost: newShippingCost };
          setLoadingFees(prev => new Set(prev).add(sku));
          const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
          setLoadingFees(prev => {
            const newSet = new Set(prev);
            newSet.delete(sku);
            return newSet;
          });
          if (fees.error) throw new Error(fees.error);
          return { ...updatedProduct, fees };
        }
        return p;
      }));

      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(p => filteredProducts.some(fp => fp.sku === p.sku)));
      localStorage.setItem('ebayProducts', JSON.stringify(updatedProducts));
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to update shipping cost for ${sku}: ${errorMsg}`);
      throw err;
    }
  };

  const handleSavePromotedRate = async (sku, newRate) => {
    if (!checkAuthAndRedirect()) return;
    try {
      const token = localStorage.getItem('token');
      await api.put(`products/${sku}`, { promotedListingRate: newRate }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedProducts = await Promise.all(products.map(async p => {
        if (p.sku === sku) {
          let updatedProduct = { ...p, promotedListingRate: newRate };
          setLoadingFees(prev => new Set(prev).add(sku));
          const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
          setLoadingFees(prev => {
            const newSet = new Set(prev);
            newSet.delete(sku);
            return newSet;
          });
          if (fees.error) throw new Error(fees.error);
          return { ...updatedProduct, fees };
        }
        return p;
      }));

      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(p => filteredProducts.some(fp => fp.sku === p.sku)));
      localStorage.setItem('ebayProducts', JSON.stringify(updatedProducts));
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to update promoted rate for ${sku}: ${errorMsg}`);
      throw err;
    }
  };

  const handleSaveInternational = async (sku, isInternational) => {
    if (!checkAuthAndRedirect()) return;
    try {
      const token = localStorage.getItem('token');
      await api.put(`products/${sku}`, { isInternationalSale: isInternational }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updatedProducts = await Promise.all(products.map(async p => {
        if (p.sku === sku) {
          let updatedProduct = { ...p, isInternationalSale: isInternational };
          setLoadingFees(prev => new Set(prev).add(sku));
          const fees = await calculateFeeClientSide(updatedProduct, storeSubscription, sellerStatus, navigate);
          setLoadingFees(prev => {
            const newSet = new Set(prev);
            newSet.delete(sku);
            return newSet;
          });
          if (fees.error) throw new Error(fees.error);
          return { ...updatedProduct, fees };
        }
        return p;
      }));

      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts.filter(p => filteredProducts.some(fp => fp.sku === p.sku)));
      localStorage.setItem('ebayProducts', JSON.stringify(updatedProducts));
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(`Failed to update international sale for ${sku}: ${errorMsg}`);
      throw err;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await checkConnection();
        if (!isConnected) return;
        await fetchProducts(page);
      } catch (err) {
        setError(`Failed to load inventory: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const query = new URLSearchParams(location.search);
    if (query.get('disconnected') === 'true') {
      setIsConnected(false);
      setError('eBay disconnected. Please reconnect.');
    } else if (query.get('reconnected') === 'true') {
      checkImportStatus();
    } else if (query.get('error')) {
      setError(decodeURIComponent(query.get('error')));
    }
  }, [location, isConnected]);

  useEffect(() => {
    const checkFeeRates = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/api/ebay/fee-rates', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { last_updated } = response.data;
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        if (new Date(last_updated) < sixMonthsAgo) {
          setError('Fee rates may be outdated for 2025. Contact support to update.');
        }
      } catch (err) {
        console.warn(`[${new Date().toISOString()}] [EbayInventory] Failed to check fee rates: ${err.message}`);
      }
    };
    checkFeeRates();
  }, []);

  if (loading || connectionLoading) {
    return (
      <Container className="mt-5">
        <h1>Loading eBay Inventory...</h1>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <h1>eBay Inventory</h1>
      <h3>Total Listings: {Number(totalItems || 0).toLocaleString()}</h3>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {!isConnected ? (
        <>
          <Alert variant="warning">Please connect your eBay account.</Alert>
          <a href="/auth/ebay"><Button variant="primary" className="mb-3">Connect eBay</Button></a>
        </>
      ) : (
        <>
          {filteredProducts.length === 0 && (
            <Alert variant="info">No eBay products found for the search criteria.</Alert>
          )}
          {/* Enhanced Search and Filters */}
          <div className="mb-4">
            <Form.Label>Search and Filter Products:</Form.Label>
            <InputGroup className="mb-2 position-relative">
              <Form.Control
                type="text"
                placeholder="Search by SKU or Name..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                disabled={searchLoading}
              />
              {searchLoading && (
                <InputGroup.Text>
                  <Spinner animation="border" size="sm" />
                </InputGroup.Text>
              )}
              <Button variant="secondary" onClick={handleClearFilters} disabled={searchLoading}>
                Clear All
              </Button>
              {showSuggestions && getSuggestions(searchQuery).length > 0 && (
                <Dropdown.Menu show style={{ position: 'absolute', top: '100%', width: '100%', zIndex: 1000 }}>
                  {getSuggestions(searchQuery).map((suggestion, index) => (
                    <Dropdown.Item key={index} onClick={() => handleSelectSuggestion(suggestion)}>
                      {suggestion}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              )}
            </InputGroup>
            <div className="d-flex flex-wrap gap-2">
              <Form.Group style={{ flex: '1 1 200px' }}>
                <Form.Label>Min Price ($):</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={filters.minPrice}
                  onChange={e => handleFilterChange('minPrice', e.target.value)}
                  disabled={searchLoading}
                />
              </Form.Group>
              <Form.Group style={{ flex: '1 1 200px' }}>
                <Form.Label>Max Price ($):</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="∞"
                  value={filters.maxPrice}
                  onChange={e => handleFilterChange('maxPrice', e.target.value)}
                  disabled={searchLoading}
                />
              </Form.Group>
              <Form.Group style={{ flex: '1 1 200px' }}>
                <Form.Label>Min Stock:</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="0"
                  value={filters.minStock}
                  onChange={e => handleFilterChange('minStock', e.target.value)}
                  disabled={searchLoading}
                />
              </Form.Group>
              <Form.Group style={{ flex: '1 1 200px' }}>
                <Form.Label>Max Stock:</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="∞"
                  value={filters.maxStock}
                  onChange={e => handleFilterChange('maxStock', e.target.value)}
                  disabled={searchLoading}
                />
              </Form.Group>
              <Form.Group style={{ flex: '1 1 200px' }}>
                <Form.Label>Platform:</Form.Label>
                <Form.Select
                  value={filters.platform}
                  onChange={e => handleFilterChange('platform', e.target.value)}
                  disabled={searchLoading}
                >
                  <option value="all">All</option>
                  <option value="eBay">eBay</option>
                  <option value="Shopify">Shopify</option>
                </Form.Select>
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>Store Subscription:</Form.Label>
            <Form.Select
              value={storeSubscription}
              onChange={e => {
                setStoreSubscription(e.target.value);
                setProducts(products.map(p => ({ ...p, fees: null })));
              }}
            >
              <option value="None">No Store</option>
              <option value="Starter">Starter</option>
              <option value="Basic">Basic</option>
              <option value="Premium">Premium</option>
              <option value="Anchor">Anchor</option>
              <option value="Enterprise">Enterprise</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Seller Status:</Form.Label>
            <Form.Select
              value={sellerStatus}
              onChange={e => {
                setSellerStatus(e.target.value);
                setProducts(products.map(p => ({ ...p, fees: null })));
              }}
            >
              <option value="Standard">Standard</option>
              <option value="TopRatedPlus">Top Rated Plus</option>
              <option value="BelowStandard">Below Standard</option>
              <option value="HighReturns">High Returns</option>
            </Form.Select>
          </Form.Group>
          <ButtonGroup className="mb-3">
            <Button variant="success" onClick={handleImportEbay} disabled={loading}>
              {loading ? 'Importing...' : 'Import from eBay'}
            </Button>
            <a href="/auth/ebay/disconnect" style={{ textDecoration: 'none' }}>
              <Button variant="outline-danger">Disconnect eBay</Button>
            </a>
            <RefreshConnectionButton platform="eBay" onRefresh={() => fetchProducts(page)} />
          </ButtonGroup>
          <div className="mb-3">
            <p>Total Listings: {Number(totalItems || 0).toLocaleString()}</p>

          </div>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Price ($)</th>
                <th>Cost ($)</th>
                <th>Shipping ($)</th>
                <th>Promoted Rate (%)</th>
                <th>International</th>
                <th>Total Fees ($)</th>
                <th>Net Profit ($)</th>
                <th>ROI (%)</th>
                <th>Stock</th>
                <th>Platform</th>
                <th>Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="14" className="text-center">No eBay products found.</td>
                </tr>
              ) : (
                filteredProducts.map(product => (
                  <tr key={product.sku}>
                    <td>{product.sku}</td>
                    <td>{product.name}</td>
                    <td>{(parseFloat(product.price) || 0).toFixed(2)}</td>
                    <td><EditableCostCell product={product} onSave={handleSaveCost} /></td>
                    <td><EditableShippingCell product={product} onSave={handleSaveShipping} /></td>
                    <td><EditablePromotedCell product={product} onSave={handleSavePromotedRate} /></td>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={product.isInternationalSale || false}
                        onChange={e => handleSaveInternational(product.sku, e.target.checked)}
                      />
                    </td>
                    <td>
                      {product.fees ? (
                        product.fees.error ? (
                          <span style={{ color: 'red' }}>{product.fees.error}</span>
                        ) : (
                          <span
                            onClick={() => {
                              setSelectedFees(product.fees);
                              setShowFeeModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {product.fees.totalFee.toFixed(2)}
                          </span>
                        )
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleCalculateFees(product)}
                          disabled={loadingFees.has(product.sku)}
                        >
                          {loadingFees.has(product.sku) ? <Spinner size="sm" /> : 'Calculate Fees'}
                        </Button>
                      )}
                    </td>
                    <td>{product.fees && !product.fees.error ? product.fees.netProfit.toFixed(2) : 'N/A'}</td>
                    <td>{product.fees && !product.fees.error && typeof product.fees.roi_percentage === 'number' ? product.fees.roi_percentage.toFixed(2) : 'N/A'}</td>
                    <td>{product.stock || 0}</td>
                    <td>{product.platform?.split(',').join(', ') || 'eBay'}</td>
                    <td><AuthenticatedImage src={product.displayImage} alt={`Image for ${product.sku}`} sku={product.sku} platform="eBay" /></td>
                    <td>
                      <Button variant="danger" size="sm" onClick={() => alert('Delete not implemented')}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <Modal show={showFeeModal} onHide={() => setShowFeeModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Fee Breakdown</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {selectedFees && (
                <>
                  <ul>
                    {selectedFees.feesBreakdown.map((fee, index) => (
                      <li key={index}>{`${fee.name}: $${parseFloat(fee.fee.value).toFixed(2)}`}</li>
                    ))}
                  </ul>
                  {selectedFees.warning && <Alert variant="warning">{selectedFees.warning}</Alert>}
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowFeeModal(false)}>Close</Button>
            </Modal.Footer>
          </Modal>
          {totalPages > 1 && (
            <Pagination className="justify-content-center">
              <Pagination.First
                onClick={() => {
                  setPage(1);
                  debouncedFetchProducts(1, searchQuery, filters);
                }}
                disabled={page === 1}
              />
              <Pagination.Prev
                onClick={() => {
                  if (page > 1) {
                    setPage(page - 1);
                    debouncedFetchProducts(page - 1, searchQuery, filters);
                  }
                }}
                disabled={page === 1}
              />
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                .map((p, _, filtered) => (
                  <React.Fragment key={p}>
                    {filtered.indexOf(p) > 0 && p - filtered[filtered.indexOf(p) - 1] > 1 && (
                      <Pagination.Ellipsis disabled />
                    )}
                    <Pagination.Item
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                        debouncedFetchProducts(p, searchQuery, filters);
                      }}
                    >
                      {p}
                    </Pagination.Item>
                  </React.Fragment>
                ))}
              <Pagination.Next
                onClick={() => {
                  if (page < totalPages) {
                    setPage(page + 1);
                    debouncedFetchProducts(page + 1, searchQuery, filters);
                  }
                }}
                disabled={page === totalPages}
              />
              <Pagination.Last
                onClick={() => {
                  setPage(totalPages);
                  debouncedFetchProducts(totalPages, searchQuery, filters);
                }}
                disabled={page === totalPages}
              />
            </Pagination>
          )}
        </>
      )}
    </Container>
  );
}

// ErrorBoundary component (unchanged)
class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error: error.message };
  }

  componentDidCatch(error, errorInfo) {
    console.error(`[${new Date().toISOString()}] [ErrorBoundary] Uncaught error: ${error.message}`, errorInfo);
  }

  render() {
    if (this.state.error) {
      return (
        <Container className="mt-5">
          <Alert variant="danger">
            <h4>Application Error</h4>
            <p>{this.state.error}</p>
            <p>Please refresh or contact support.</p>
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default function EbayInventoryWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <EbayInventory />
    </ErrorBoundary>
  );
}